import { AwaitableStandardEvent, CancellableEventArgs, Event } from "Global/Base/Events";

export type IListenerInfo = {
    key?: string;
    removeListener: (options?: EventListenerOptions) => void;
};

export type StandardEventBase = {
    remove: (callback: Function) => void;
    add: (callback: Function, oneTimeOnly?: boolean, asFirst?: boolean) => void;
};

export type IEventListenerInfo = IListenerInfo & {
    event: StandardEventBase;
    callback: Function;
};

class EventListenerInfo implements IEventListenerInfo {
    private _event: StandardEventBase;
    private _callback: Function;

    public get event(): StandardEventBase {
        return this._event;
    }
    public get callback(): Function {
        return this._callback;
    }

    public key: string;

    constructor(event: StandardEventBase, callback: Function) {
        this._event = event;
        this._callback = callback;
    }

    public removeListener(): void {
        this.event.remove(this.callback);
        this._event = null;
        this._callback = null;
    }
}

export type StandardEventsControllerAddEventParams = {
    /** Zda má být listener zavolán pouze jednou a pak uvolněn. */
    oneTimeOnly?: boolean;
    /** Umožňuje zařadit listener na první místo. */
    asFirst?: boolean;
};

export type StandardEventsControllerAddWithKeyParams = StandardEventsControllerAddEventParams & {
    /** Zároveň odváže všechny předchozí eventy se zadaným `key`. */
    removePrevious?: boolean;
};

/**
 * Slouží k navazování tesco eventů (`Event`, `AwaitableStandardEvent`). Pamatuje si všechny eventy a při dispose zase všechny odváže.
 * Pomáhá zabránit tomu, že někde zůstanou navázané eventy již disposnutých instancí.
 *
 * Také je možné navazovat eventy s unikátním klíčem `key` a kdykoliv odvázat jen eventy s daným klíčem.
 * @includeToDoc
 */
export class StandardEventsController {
    protected _listeners: IListenerInfo[] = [];
    private _isDisposed = false;

    /**
     * Příznak, zda už to má `EventsController` za sebou. Pak už není možné přidávat další eventy.
     */
    public get isDisposed(): boolean {
        return this._isDisposed;
    }

    /**
     * Naváže callback na event a zapamatuje si jej.
     */
    public add<K, L>(event: AwaitableStandardEvent<K, L>, callback: (cancellableEventArgs: CancellableEventArgs, sender: K, prm: L) => void, params?: StandardEventsControllerAddEventParams): IEventListenerInfo;
    public add<K, L>(event: Event<K, L>, callback: (sender: K, prm: L) => void, params?: StandardEventsControllerAddEventParams): IEventListenerInfo;
    // eslint-disable-next-line unused-imports/no-unused-vars
    public add<K, L>(event: StandardEventBase, callback: Function, params?: StandardEventsControllerAddEventParams): IEventListenerInfo;
    // @ts-ignore
    // eslint-disable-next-line unused-imports/no-unused-vars
    public add<K, L>(event: StandardEventBase, callback: Function, params?: StandardEventsControllerAddEventParams): IEventListenerInfo {
        if (!event || this.isDisposed)
            return null;
        event.add(callback, params?.oneTimeOnly, params?.asFirst);
        const listenerInfo = new EventListenerInfo(event, callback);
        this._listeners.push(listenerInfo);
        return listenerInfo;
    }

    /**
     * Naváže callback na event a zapamatuje si jej pod klíčem `key`.
     * @param key Unikátní klíč, pod kterým se callback/callbacky uloží.
     */
    public addWithKey<K, L>(key: string, event: AwaitableStandardEvent<K, L>, callback: (cancellableEventArgs: CancellableEventArgs, sender: K, prm: L) => void, params?: StandardEventsControllerAddWithKeyParams): IEventListenerInfo;
    public addWithKey<K, L>(key: string, event: Event<K, L>, callback: (sender: K, prm: L) => void, params?: StandardEventsControllerAddWithKeyParams): IEventListenerInfo;
    // eslint-disable-next-line unused-imports/no-unused-vars
    public addWithKey<K, L>(key: string, event: StandardEventBase, callback: Function, params?: StandardEventsControllerAddWithKeyParams): IEventListenerInfo;
    // @ts-ignore
    // eslint-disable-next-line unused-imports/no-unused-vars
    public addWithKey<K, L>(key: string, event: StandardEventBase, callback: Function, params?: StandardEventsControllerAddWithKeyParams): IEventListenerInfo {
        if (params?.removePrevious)
            this.removeByKey(key);
        if (!event || this.isDisposed)
            return null;
        const listenerInfo = this.add(event, callback, params);
        listenerInfo.key = key;
        return listenerInfo;
    }

    /**
     * Odváže veškeré zapamatované eventy, které byly přidány pod stejným `key`.
     * @param keys Unikátní klíče přidaných eventů, které se mají odvázat.
     */
    public removeByKey(...keys: string[]): void {
        const toRemove: IListenerInfo[] = [];
        // eslint-disable-next-line array-callback-return
        this._listeners.reduceRight<void>((_, listenerInfo, index, listeners) => {
            if (keys.includes(listenerInfo.key)) {
                toRemove.push(...listeners.splice(index, 1));
            }
        }, null);
        toRemove.forEach(listenerInfo => listenerInfo.removeListener());
    }

    /**
     * Odváže všechny zapamatované eventy (ať už s `key` nebo bez).
     */
    public removeAllListeners(): void {
        const events = this._listeners?.splice(0, this._listeners.length);
        events?.forEach(listenerInfo => listenerInfo.removeListener());
    }

    /**
     * Odváže všechny zapamatované eventy (ať už s `key` nebo bez) a zabrání přidání dalších.
     */
    public dispose(): void {
        if (this._isDisposed) {
            return;
        }

        this._isDisposed = true;
        this.removeAllListeners();
    }
}
